import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Buttons, GLOBAL_IMAGES } from "../../assets/asset";

import { SkeletonYears } from "../../utils/SkeletonLoader";
import Header from "./YearsHeader";

import { useDispatch, useSelector } from "react-redux";
import { setActiveYear } from "../../store/slice/scrollPositionSlice";

import { useYears } from "../../features/queryHooks/useYears";
import { useUpdateUser } from "../../features/auth/useLogin";
import { toast } from "react-toastify";
import {
  setCurrentYear,
  setShowKnowledge,
} from "../../store/slice/profileSlice";
import { content } from "../../utils/constants";
import {
  formatSubscriptionDate,
  getPlanExpiryDate,
} from "../../utils/subscriptionDates";
import {
  getCanonicalYearId,
  getCurrentUnlockedYearId,
  isPaidBasicUser,
  isPaidProUser,
} from "../../utils/basicPlanAccess";
import DemoManager from "./DemoManager";
import { useSubscription } from "../../context/SubscriptionContext";

const CHECKOUT_RETURN_QUERY_KEYS = [
  "subscription",
  "success",
  "redirect_status",
  "session_id",
  "setup_intent",
  "setup_intent_client_secret",
  "payment_intent",
  "payment_intent_client_secret",
];

const SUCCESS_SPARKLES = [
  {
    left: "6%",
    size: "18px",
    delay: "0s",
    duration: "3.1s",
    color: "#ffe16f",
  },
  {
    left: "14%",
    size: "14px",
    delay: "0.45s",
    duration: "3.8s",
    color: "#ffb84d",
  },
  {
    left: "24%",
    size: "20px",
    delay: "0.9s",
    duration: "3.3s",
    color: "#7ee1ff",
  },
  {
    left: "34%",
    size: "16px",
    delay: "1.2s",
    duration: "4.2s",
    color: "#ffd98d",
  },
  {
    left: "46%",
    size: "22px",
    delay: "0.3s",
    duration: "3.5s",
    color: "#fff4a3",
  },
  {
    left: "58%",
    size: "15px",
    delay: "1.4s",
    duration: "4s",
    color: "#ffcf70",
  },
  {
    left: "68%",
    size: "18px",
    delay: "0.75s",
    duration: "3.6s",
    color: "#9fe4ff",
  },
  {
    left: "77%",
    size: "13px",
    delay: "1.65s",
    duration: "4.4s",
    color: "#ffe16f",
  },
  {
    left: "87%",
    size: "21px",
    delay: "0.6s",
    duration: "3.4s",
    color: "#ffc857",
  },
  {
    left: "94%",
    size: "16px",
    delay: "1.1s",
    duration: "4.1s",
    color: "#fff0a0",
  },
];

const hasCheckoutReturnQuery = (searchParams) => {
  const hasStripeQueryFlag = CHECKOUT_RETURN_QUERY_KEYS.some((key) =>
    searchParams.has(key)
  );
  const subscriptionState = searchParams.get("subscription");
  return hasStripeQueryFlag || subscriptionState === "success" || subscriptionState === "cancel";
};

const getPlanTypeLabel = (subscription = {}) => {
  const values = [
    subscription?.plan_type,
    subscription?.planType,
    subscription?.plan_name,
    subscription?.subscription_plan,
    subscription?.plan,
    subscription?.plan_key,
    subscription?.price_plan,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (values.includes("pro")) return "Pro";
  if (values.includes("basic")) return "Basic";
  return "N/A";
};

const getPlanIntervalLabel = (subscription = {}) => {
  const values = [
    subscription?.plan_name,
    subscription?.subscription_plan,
    subscription?.plan,
    subscription?.plan_key,
    subscription?.price_plan,
    subscription?.interval,
    subscription?.billing_interval,
    subscription?.billingInterval,
    subscription?.cycle,
    subscription?.period,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (values.includes("year")) return "Yearly";
  if (values.includes("month")) return "Monthly";
  return "N/A";
};

const getPlanSummaryLabel = (subscription = {}) => {
  const planType = getPlanTypeLabel(subscription);
  const interval = getPlanIntervalLabel(subscription);

  if (planType === "N/A" && interval === "N/A") return "N/A";
  if (planType !== "N/A" && interval !== "N/A") return `${planType} ${interval}`;
  return planType !== "N/A" ? planType : interval;
};

const getStatusLabel = (status = "") => {
  const normalized = String(status).trim().toLowerCase();
  if (!normalized) return "N/A";
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
};

// Loading component
const LoadingScreen = () => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-blue-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
      <h2 className="text-2xl font-bold text-blue-800 mb-2">Loading MathDingo</h2>
      <p className="text-blue-600">Please wait while we prepare everything...</p>
    </div>
  </div>
);

const YearsDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user, isNewUser } = useSelector((state) => state.user);
  const {
    subscription,
    checkSubscription,
    isChecking,
    hasCheckedOnce,
    normalizedPlanType,
  } = useSubscription();
  const location = useLocation();
  const subscriptionStatus = subscription?.subscription_status;
  const hasActiveOrTrialSubscription =
    subscriptionStatus === "active" || subscriptionStatus === "trialing";
  const subscriptionResolved = hasCheckedOnce && !isChecking;
  const isBasicPlan = normalizedPlanType === "basic" || isPaidBasicUser(user);
  const isProPlan = normalizedPlanType === "pro" || isPaidProUser(user);
  const shouldForceLockedBubbles =
    subscriptionResolved && !hasActiveOrTrialSubscription;
  const shouldShowBuyPlan = subscriptionResolved && shouldForceLockedBubbles;

  const [showLessonContent, setShowLessonContent] = React.useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [checkoutNotice, setCheckoutNotice] = useState(null);

  // Preload images function
  const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = reject;
      img.src = src;
    });
  };

  const preloadAllImages = async () => {
    const imagesToPreload = [
      GLOBAL_IMAGES.onboarding_bg,
      GLOBAL_IMAGES.contentCardImage,
      GLOBAL_IMAGES.exclamationMark,
      GLOBAL_IMAGES.yearContainerImage,
      GLOBAL_IMAGES.lesson_lock,
    ];

    try {
      await Promise.all(imagesToPreload.map(src => preloadImage(src)));
      setImagesLoaded(true);
    } catch (error) {
      console.error('Error preloading images:', error);
      // Still show the page even if some images fail to load
      setImagesLoaded(true);
    }
  };

  // Preload images on component mount
  useEffect(() => {
    preloadAllImages();
  }, []);

  const { showDemo, demoCompleted, handleDemo, DemoComponent } = DemoManager({
    isNewUser,
  });

  const yearsQuery = useYears();
  const { refetch: refetchYears } = yearsQuery;
  const updateUserMutation = useUpdateUser();

  useEffect(() => {
    dispatch(setActiveYear(-1));
  }, []);

  const clearCheckoutStorage = React.useCallback(() => {
    try {
      sessionStorage.removeItem("pendingStripeCheckout");
      sessionStorage.removeItem("pendingStripeCheckoutAt");
    } catch (_) {
      // Ignore storage errors.
    }
  }, []);

  const clearCheckoutQueryParams = React.useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    let changed = false;

    CHECKOUT_RETURN_QUERY_KEYS.forEach((key) => {
      if (searchParams.has(key)) {
        searchParams.delete(key);
        changed = true;
      }
    });

    if (!changed) return;

    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    checkSubscription().catch(() => null);
  }, [checkSubscription]);

  useEffect(() => {
    if (!subscriptionResolved) return;
    refetchYears();
  }, [subscriptionResolved, hasActiveOrTrialSubscription, refetchYears]);

  useEffect(() => {
    const configuredFrontendUrl = process.env.REACT_APP_FRONTEND_URL || "";
    if (!configuredFrontendUrl || typeof window === "undefined") return;

    let configuredOrigin = "";
    try {
      configuredOrigin = new URL(configuredFrontendUrl).origin;
    } catch (_) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const isReturnFromCheckout = hasCheckoutReturnQuery(searchParams);

    if (!isReturnFromCheckout) return;
    if (window.location.origin === configuredOrigin) return;

    const targetUrl = `${configuredOrigin}${location.pathname}${location.search}${location.hash || ""}`;
    window.location.replace(targetUrl);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isReturnFromCheckout = hasCheckoutReturnQuery(searchParams);
    const subscriptionState = String(searchParams.get("subscription") || "").toLowerCase();
    const isCancelledCheckout = subscriptionState === "cancel";
    let hasPendingCheckoutFlag = false;
    let pendingCheckoutAt = 0;
    try {
      hasPendingCheckoutFlag = Boolean(
        sessionStorage.getItem("pendingStripeCheckout")
      );
      pendingCheckoutAt = Number(
        sessionStorage.getItem("pendingStripeCheckoutAt") || 0
      );
    } catch (_) {
      hasPendingCheckoutFlag = false;
      pendingCheckoutAt = 0;
    }
    const maxPendingAgeMs = 30 * 60 * 1000;
    const isPendingFlagFresh =
      pendingCheckoutAt > 0 && Date.now() - pendingCheckoutAt <= maxPendingAgeMs;

    if (isCancelledCheckout) {
      clearCheckoutStorage();
      clearCheckoutQueryParams();
      setCheckoutNotice({
        type: "warning",
        title: "Payment cancelled",
        message:
          "No payment was completed. Your subscription is still inactive until checkout succeeds.",
      });
      toast.info("Payment was cancelled before completion.", {
        autoClose: 3500,
        theme: "colored",
        hideProgressBar: true,
        className: "font-mali",
      });
      return;
    }

    if (!isReturnFromCheckout && hasPendingCheckoutFlag && !isPendingFlagFresh) {
      clearCheckoutStorage();
      return;
    }

    if (!isReturnFromCheckout && !hasPendingCheckoutFlag) return;

    let cancelled = false;

    const runPostCheckoutSync = async () => {
      const maxAttempts = 8;
      const delayMs = 1500;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (cancelled) return;
        const latestData = await checkSubscription().catch(() => null);
        const latestStatus = latestData?.subscription_status;
        const latestRequiresPaymentMethod = Boolean(
          latestData?.requires_payment_method
        );
        const canAccessApp =
          Boolean(latestData?.has_access) ||
          ((latestStatus === "active" || latestStatus === "trialing") &&
            !latestRequiresPaymentMethod);

        if (canAccessApp) {
          clearCheckoutStorage();
          clearCheckoutQueryParams();
          setCheckoutNotice({
            type: "success",
            title: "Payment successful",
            message:
              "Your subscription is active and you now have access to lessons.",
            details: {
              plan: getPlanSummaryLabel(latestData),
              status: getStatusLabel(latestStatus),
              trialEndDate: formatSubscriptionDate(latestData?.trial_end_date),
              planExpiryDate: formatSubscriptionDate(
                getPlanExpiryDate(latestData)
              ),
            },
          });
          toast.success("Payment confirmed. Subscription activated.", {
            autoClose: 3500,
            theme: "colored",
            hideProgressBar: true,
            className: "font-mali",
          });
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      clearCheckoutStorage();
      clearCheckoutQueryParams();
      setCheckoutNotice({
        type: "info",
        title: "Payment is processing",
        message:
          "Your payment may still be confirming. Please check Billing Details in a moment to verify activation.",
      });
      toast.info("Payment confirmation is still syncing. Check billing details shortly.", {
        autoClose: 4000,
        theme: "colored",
        hideProgressBar: true,
        className: "font-mali",
      });
    };

    runPostCheckoutSync();

    return () => {
      cancelled = true;
    };
  }, [
    location.search,
    checkSubscription,
    clearCheckoutStorage,
    clearCheckoutQueryParams,
  ]);

  const checkoutNoticeStyles = {
    success: {
      container:
        "bg-[#E9F9EA] border-[#2E9B50] text-[#184F2C] shadow-[0_6px_18px_rgba(46,155,80,0.2)]",
      action:
        "bg-[#2E9B50] border-[#236F3A] text-white hover:bg-[#236F3A]",
    },
    warning: {
      container:
        "bg-[#FFF6E8] border-[#D9822B] text-[#7A470F] shadow-[0_6px_18px_rgba(217,130,43,0.2)]",
      action:
        "bg-[#D9822B] border-[#A8641D] text-white hover:bg-[#A8641D]",
    },
    info: {
      container:
        "bg-[#E9F0FF] border-[#3366CC] text-[#1D3970] shadow-[0_6px_18px_rgba(51,102,204,0.2)]",
      action:
        "bg-[#3366CC] border-[#264E9C] text-white hover:bg-[#264E9C]",
    },
  };

  const visibleYears = React.useMemo(
    () => yearsQuery.data?.data?.slice(4) ?? [],
    [yearsQuery.data]
  );
  const firstVisibleYearId = React.useMemo(
    () => getCanonicalYearId(visibleYears[0]),
    [visibleYears]
  );
  const currentUnlockedYearId = getCurrentUnlockedYearId(user);
  const basicUnlockedYearId = currentUnlockedYearId || firstVisibleYearId;
  const shouldApplyBasicYearLock =
    subscriptionResolved &&
    hasActiveOrTrialSubscription &&
    isBasicPlan &&
    basicUnlockedYearId > 0;
  const shouldUnlockAllYears =
    subscriptionResolved &&
    hasActiveOrTrialSubscription &&
    (isProPlan || !shouldApplyBasicYearLock);

  // Helper function to determine if a year is locked
  const isYearLocked = (year) => {
    if (shouldForceLockedBubbles) return true;
    if (shouldUnlockAllYears) return false;
    if (shouldApplyBasicYearLock) {
      return getCanonicalYearId(year) !== basicUnlockedYearId;
    }
    // Check if isOpen field exists and is 0 (locked)
    return year?.fields?.isOpen === 0 || year?.fields?.isOpen === "0";
  };

  const newUpdateUserYearSelection = async (yearId) => {
    updateUserMutation.mutate(
      { id: user?.UserId || user.pk, yearid: yearId },
      {
        onSuccess: () => {
          navigate(`/lessonDashboard?yearId=${yearId}`, { state: { yearId } });
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.message ||
              "Error updating user year. Please try again.",
            {
              ariaLabel: "Update Error",
              autoClose: 2000,
              theme: "colored",
              hideProgressBar: true,
              className: "font-mali",
            }
          );
        },
      }
    );
  };

  const handleLessonClick = () => {
    newUpdateUserYearSelection(showLessonContent);
    dispatch(setShowKnowledge(true));
    setShowLessonContent(false);
  };

  const handleYearClick = (year) => {
    // Check if year is locked before allowing click
    const selectedYearId = getCanonicalYearId(year);
    if (isYearLocked(year)) {
      if (shouldShowBuyPlan) {
        navigate("/subscription");
      }
      // toast.warning(`${year.fields.year} is currently locked and not available.`, {
      //   ariaLabel: "Year Locked",
      //   autoClose: 2000,
      //   theme: "colored",
      //   hideProgressBar: true,
      //   className: "font-mali",
      // });
      return;
    }

    if (demoCompleted) {
      // Navigate to DemoDashboard with year ID if demo was just completed
      navigate("/demoDashboard", { state: { yearId: selectedYearId } });
    } else {
      // Original behavior - show lesson content
      setShowLessonContent(selectedYearId);
    }
    dispatch(setCurrentYear(year));
  };

  const handleBuyPlanClick = () => {
    navigate("/subscription");
  };

  // Show loading screen until images are loaded
  if (!imagesLoaded) {
    return <LoadingScreen />;
  }

  return (
    <>
      <div
        className="min-h-screen  flex flex-col items-center bg-blue-50 font-mali overflow-auto"
        style={{
          backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        role="main"
        aria-labelledby="years-dashboard-title"
      >
        {showLessonContent && (
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/30 overflow-y-auto">
            <button
              type="button"
              aria-label="Close contents popup"
              onClick={() => setShowLessonContent(null)}
              className="absolute top-[1vw] right-[1vw] z-50 min-h-10 min-w-10 flex items-center justify-center"
            >
              <img
                src={Buttons.cross}
                alt="Close"
                className="h-10 w-10 md:h-14 md:w-14 lg:h-16 lg:w-16 cursor-pointer"
              />
            </button>
            <div
              style={{
                backgroundImage: `url(${GLOBAL_IMAGES.contentCardImage})`,
              }}
              className=" bg-contain bg-center bg-no-repeat 
        flex flex-col items-center justify-center 
        
    
        min-h-[400px] lg:min-h-[400px] max-w-2xl w-full 2xl:min-h-[650px]"
            >
              {" "}
              <div
                className="text-center xl:w-7/12 w-6/12 2xl:w-8/12  
             xl:h-[25rem] h-[20rem] lg:mt-10 xl:mt-10 
             2xl:mt-2 mb-36 xl:mb-40 flex flex-col my-auto content-fix"
              >
                <h2 className="text-center mb-4 w-full text-xl xl:text-3xl font-bold text-primary-dark-blue">
                  CONTENTS
                </h2>
                <div className="overflow-y-auto gap-2 flex flex-col text-gold">
                  {content[showLessonContent - 1]?.map((item, index) => (
                    <div
                      key={index}
                      className=" bg-primary-button-dark-blue rounded-full "
                    >
                      <p className="text-[#f4b331] text-nowrap font-semibold mx-2 my-1">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="text-center  w-full">
              <button
                disabled={updateUserMutation.isPending}
                className="bg-primary-button-dark-blue  lg:px-10 rounded-3xl lg:mt-4 py-2 border-4 border-yellow-400"
                onClick={handleLessonClick}
              >
                <span className="font-mali font-bold text-xl lg:text-2xl text-white">
                  {!updateUserMutation.isPending ? "Lets Go" : "Wait..."}
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Render the demo component */}
        {DemoComponent}

        {/* Header */}
        <div className="w-full">
          <Header showDemo={showDemo} showLessonContent={showLessonContent} />
        </div>

        {/* Main Content */}
        <div
          className="flex mb-8 flex-col items-center justify-center gap-2 self-center space-y-8 w-full px-11"
          aria-describedby="years-dashboard-description"
        >
          <h1
            id="years-dashboard-title"
            className="text-black text-center text-4xl md:text-5xl font-bold leading-none"
          >
            Select {user.user_name}'s Math grade
          </h1>
          <p
            id="years-dashboard-description"
            className="text-black text-center font-bold leading-none text-lg md:text-2xl"
          >
            We will curate {user.user_name}'s learning experience based on the
            grade and assessment
          </p>
          {checkoutNotice && (
            <div
              role="status"
              aria-live="polite"
              className={`w-full max-w-3xl border-2 rounded-2xl px-5 py-4 md:px-6 md:py-5 ${
                checkoutNotice.type === "success"
                  ? "payment-success-card relative overflow-hidden isolate"
                  : ""
              } ${
                checkoutNoticeStyles[checkoutNotice.type]?.container ||
                checkoutNoticeStyles.info.container
              }`}
            >
              {checkoutNotice.type === "success" && (
                <div
                  aria-hidden="true"
                  className="payment-success-sparkles"
                  data-testid="payment-success-sparkles"
                >
                  {SUCCESS_SPARKLES.map((sparkle, index) => (
                    <span
                      key={index}
                      className="payment-success-sparkle"
                      style={{
                        "--sparkle-left": sparkle.left,
                        "--sparkle-size": sparkle.size,
                        "--sparkle-delay": sparkle.delay,
                        "--sparkle-duration": sparkle.duration,
                        "--sparkle-color": sparkle.color,
                      }}
                    />
                  ))}
                </div>
              )}
              <div className="relative z-[1]">
                <h2 className="text-xl md:text-2xl font-extrabold">
                  {checkoutNotice.title}
                </h2>
                <p className="mt-1 text-sm md:text-base font-semibold">
                  {checkoutNotice.message}
                </p>
                {checkoutNotice.details && (
                  <div className="mt-3 text-sm md:text-base font-bold leading-7">
                    <p>Plan: {checkoutNotice.details.plan}</p>
                    <p>Status: {checkoutNotice.details.status}</p>
                    <p>Trial End Date: {checkoutNotice.details.trialEndDate}</p>
                    <p>Plan Expiry Date: {checkoutNotice.details.planExpiryDate}</p>
                  </div>
                )}
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/billing-details")}
                    className={`px-4 py-2 rounded-lg border-2 font-bold text-sm md:text-base transition ${
                      checkoutNoticeStyles[checkoutNotice.type]?.action ||
                      checkoutNoticeStyles.info.action
                    }`}
                  >
                    View Billing Details
                  </button>
                  <button
                    type="button"
                    onClick={() => setCheckoutNotice(null)}
                    className="px-4 py-2 rounded-lg border-2 border-[#0B2B66] bg-white/90 text-[#0B2B66] font-bold text-sm md:text-base hover:bg-white transition"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}
          {shouldShowBuyPlan && (
            <button
              type="button"
              onClick={handleBuyPlanClick}
              className="relative px-10 py-3 md:px-12 md:py-4 rounded-full border-[5px] border-[#0B2B66] text-white font-extrabold text-lg md:text-2xl uppercase tracking-wide shadow-[0_8px_0_#0B2B66,0_14px_20px_rgba(14,58,138,0.35)] hover:-translate-y-1 hover:shadow-[0_10px_0_#0B2B66,0_16px_24px_rgba(14,58,138,0.35)] active:translate-y-[3px] active:shadow-[0_4px_0_#0B2B66,0_8px_12px_rgba(14,58,138,0.25)] transition-all duration-200"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #fff176 0%, #ffd54f 32%, #ffb300 64%, #fb8c00 100%)",
                backgroundPosition: "center",
                textShadow: "1px 1px 0 #0B2B66, 2px 2px 0 #0B2B66",
                letterSpacing: "0.04em",
              }}
              aria-label="Buy a plan"
            >
              Buy a plan
            </button>
          )}

          {/* Info Box */}
          <div
            className="w-full flex justify-center py-6 gap-5 items-center px-5"
            style={{
              backgroundColor: "#975113",
              maxWidth: "70%",
              borderRadius: "10px",
            }}
            role="region"
            aria-label="Information about grade selection"
          >
            <img
              alt="Exclamation mark icon"
              src={GLOBAL_IMAGES.exclamationMark}
              className="h-9 self-center"
            />
            <p className="text-white text-center text-sm md:text-xl">
              You can always go back and change the math grade of your child
              from settings, if you feel you need to change the grade.
            </p>
          </div>

          {/* Loading and Error Handling */}
          {yearsQuery.isPending && (
            <SkeletonYears aria-label="Loading years data" />
          )}
          {yearsQuery.isError && (
            <p
              className="text-red-500"
              role="alert"
              aria-live="assertive"
              data-testid="error-message"
            >
              Error: {yearsQuery.error.message}
            </p>
          )}

          {/* Years List */}
          {!yearsQuery.isPending && !yearsQuery.isError && (
            <div
              className={`w-screen overflow-x-scroll py-6 pb-12  overflow-hidden hover:overflow-auto ${
                showDemo === true || showDemo === 1 || !showDemo
                  ? "z-10"
                  : "z-10"
              }`}
              role="list"
              aria-label="List of available years"
            >
              <div className="flex z-40 justify-center space-x-10 mx-9">
                {visibleYears.map((year, index) => {
                  const isLocked = isYearLocked(year);
                  const yearId = getCanonicalYearId(year);
                  const yearLabel = year?.fields?.year;
                  
                  return (
                    <div
                      key={year.id || yearId}
                      className={`size-24 mb-6 md:w-36 md:h-36 lg:size-40 flex relative items-center justify-center rounded-full ${
                        isLocked ? "cursor-not-allowed" : "cursor-pointer"
                      } ${
                        index % 2 === 0 ? "-top-4" : "top-10"
                      }`}
                      style={{
                        backgroundImage: `url(${GLOBAL_IMAGES.yearContainerImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        flexShrink: 0,
                      }}
                      onClick={() => handleYearClick(year)}
                      role="listitem"
                      aria-label={`Year ${yearLabel} ${isLocked ? "(locked)" : ""}`}
                      aria-disabled={isLocked}
                      data-testid={`year-${yearId}`}
                    >
                      {/* Lock Icon - Show when year is locked */}
                      {isLocked && (
                        <img
                          src={GLOBAL_IMAGES.lesson_lock}
                          alt="lock"
                          className="absolute w-12 h-12 md:w-16 md:h-16 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"
                        />
                      )}
                      
                      <p
                        className="relative z-[1] font-bold text-center text-sm md:text-xl lg:text-2xl text-white"
                        aria-hidden="true"
                      >
                        {yearLabel}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

    </>
  );
};

export default YearsDashboard;
